import React, { useRef } from 'react';
import { Bed, User } from 'lucide-react';

export default function BedBox({ bed, room, floor, tenant, onBedClick, onDragStart, onDrop, onDragOver, isDragTarget }) {
  const isOccupied = bed.status === 'occupied';
  const isVacant = bed.status === 'vacant';
  const isTouchDevice = typeof window !== 'undefined'
    && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
  const touchDragStateRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startY: 0
  });

  const buildTransferPayload = (targetBedId, targetFloorName, targetRoomNumber, targetBedNumber) => ({
    tenantId: tenant.id,
    oldBedId: bed.id,
    newBedId: Number(targetBedId),
    tenantName: tenant.name,
    fromLocation: `${floor.floorName} - Room ${room.roomNumber} - Bed ${bed.bedNumber}`,
    toLocation: `${targetFloorName} - Room ${targetRoomNumber} - Bed ${targetBedNumber}`
  });

  const handleDragStart = (e) => {
    if (isOccupied && tenant) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('tenantId', String(tenant.id));
      e.dataTransfer.setData('oldBedId', String(bed.id));
      e.dataTransfer.setData('tenantName', String(tenant.name));
      e.dataTransfer.setData('fromLocation', `${floor.floorName} - Room ${room.roomNumber} - Bed ${bed.bedNumber}`);
      onDragStart({
        bedId: bed.id,
        tenantId: tenant.id,
        oldBedId: bed.id,
        tenantName: tenant.name,
        fromLocation: `${floor.floorName} - Room ${room.roomNumber} - Bed ${bed.bedNumber}`
      });
    }
  };

  const handleTouchStart = (e) => {
    if (!isOccupied || !tenant) {
      return;
    }

    e.preventDefault();

    const touch = e.touches[0];
    touchDragStateRef.current = {
      active: true,
      moved: false,
      startX: touch.clientX,
      startY: touch.clientY
    };

    onDragStart({
      bedId: bed.id,
      tenantId: tenant.id,
      oldBedId: bed.id,
      tenantName: tenant.name,
      fromLocation: `${floor.floorName} - Room ${room.roomNumber} - Bed ${bed.bedNumber}`
    });
  };

  const handleTouchMove = (e) => {
    if (!touchDragStateRef.current.active) {
      return;
    }

    e.preventDefault();

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchDragStateRef.current.startX);
    const deltaY = Math.abs(touch.clientY - touchDragStateRef.current.startY);

    if (deltaX > 10 || deltaY > 10) {
      touchDragStateRef.current.moved = true;
    }
  };

  const handleTouchEnd = (e) => {
    if (!touchDragStateRef.current.active) {
      return;
    }

    const wasMoved = touchDragStateRef.current.moved;
    touchDragStateRef.current.active = false;

    if (!wasMoved || !tenant) {
      return;
    }

    const touch = e.changedTouches[0];
    const dropElement = document.elementFromPoint(touch.clientX, touch.clientY)?.closest('[data-bed-id]');

    if (!dropElement) {
      return;
    }

    const targetBedId = Number(dropElement.getAttribute('data-bed-id'));
    const targetBedStatus = dropElement.getAttribute('data-bed-status');
    const targetFloorName = dropElement.getAttribute('data-floor-name');
    const targetRoomNumber = dropElement.getAttribute('data-room-number');
    const targetBedNumber = dropElement.getAttribute('data-bed-number');

    if (!targetBedId || targetBedId === bed.id || targetBedStatus !== 'vacant') {
      return;
    }

    onDrop(buildTransferPayload(targetBedId, targetFloorName, targetRoomNumber, targetBedNumber));
  };

  const handleDragOver = (e) => {
    if (isVacant) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (isVacant) {
      const tenantId = e.dataTransfer.getData('tenantId');
      const oldBedId = e.dataTransfer.getData('oldBedId');
      const tenantName = e.dataTransfer.getData('tenantName');
      const fromLocation = e.dataTransfer.getData('fromLocation');
      const toLocation = `${floor.floorName} - Room ${room.roomNumber} - Bed ${bed.bedNumber}`;
      
      onDrop({
        tenantId,
        oldBedId,
        newBedId: bed.id,
        tenantName,
        fromLocation,
        toLocation
      });
    }
  };

  return (
    <div
      draggable={isOccupied && !isTouchDevice}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={() => onBedClick(bed, room, floor, tenant)}
      className={`relative p-4 rounded-lg border-2 text-center transition-all cursor-pointer select-none ${
        isOccupied
          ? `bg-blue-50 border-blue-300 hover:border-blue-400 hover:shadow-md ${isTouchDevice ? 'touch-none' : 'cursor-move'}`
          : isDragTarget
          ? 'bg-green-100 border-green-400 border-dashed animate-pulse'
          : 'bg-green-50 border-green-200 hover:border-green-300'
      }`}
      data-testid={`bed-${bed.id}`}
      data-bed-id={bed.id}
      data-bed-status={bed.status}
      data-floor-name={floor.floorName}
      data-room-number={room.roomNumber}
      data-bed-number={bed.bedNumber}
    >
      {isOccupied && (
        <div className="absolute top-1 right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
          <User className="w-3 h-3 text-white" />
        </div>
      )}
      
      <Bed className={`w-8 h-8 mx-auto mb-2 ${
        isOccupied ? 'text-blue-600' : 'text-green-600'
      }`} />
      
      <p className={`text-xs font-bold mb-1 ${
        isOccupied ? 'text-blue-900' : 'text-green-900'
      }`}>
        Bed {bed.bedNumber}
      </p>
      
      {isOccupied && tenant ? (
        <p className="text-xs text-blue-700 font-medium truncate">{tenant.name}</p>
      ) : (
        <p className="text-xs text-green-700">Vacant</p>
      )}
    </div>
  );
}
